/**
 * A module-level handle to the app's React Query client, so offline-queue
 * replay handlers (which run outside the React tree, in the background runner)
 * can invalidate queries after a successful replay.
 *
 * entry-client.tsx registers the SAME QueryClient instance the UI uses via
 * setAppQueryClient(queryClient) at boot. Handlers read it lazily via
 * getAppQueryClient() — it may legitimately be null in tests / SSR / before
 * boot registration, so callers must null-check.
 *
 * Why a registry rather than getQueryClient() from lib/queryClient.ts: the
 * app boots with `createQueryClient()` (its own instance), which is NOT the
 * lib singleton. Invalidating the wrong instance would be a silent no-op, so
 * we thread through the exact instance the components render against.
 */

import type { QueryClient } from '@tanstack/react-query'

let appQueryClient: QueryClient | null = null

/** Register the app's QueryClient so background replay handlers can use it. */
export function setAppQueryClient(client: QueryClient): void {
  appQueryClient = client
}

/** Get the registered app QueryClient, or null if none has been registered. */
export function getAppQueryClient(): QueryClient | null {
  return appQueryClient
}
